import { useNavigate, useRouterState } from "@tanstack/react-router";
import {
  CANONICAL_PRODUCT_CATEGORIES,
  normalizeProductCategory,
  type ProductCategory,
} from "@the-drop/customer-shared/normalize-product-category";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AgeGateScreen } from "../components/AgeGateScreen";
import { FilterPillGroup } from "../components/FilterPillGroup";
import { ProductCardSkeleton } from "../components/ProductCardSkeleton";
import { ProductGrid, type MarketplaceItem } from "../dispensary/MenuScreen";
import type { PublicMenuLineItem, PublicMenuPayload } from "../lib/dispensary-menu-types";
import { isSessionAgeVerified } from "../lib/age-gate-session";
import { writeCheckoutShopMeta } from "../lib/checkout-shop-meta";
import {
  DispensaryMenuHttpError,
  DispensaryMenuNotFoundError,
  fetchDispensaryMenu,
} from "../lib/fetch-dispensary-menu";
import { fetchPublicDispensaryCatalog, type PublicDispensaryCatalogEntry } from "../lib/fetch-public-dispensary-catalog";
import { useCartStore } from "../store/cart-store";

type ShopBlock =
  | { entry: PublicDispensaryCatalogEntry; menu: PublicMenuPayload }
  | { entry: PublicDispensaryCatalogEntry; menu: null; loadError: string };

/** Storage slug for the marketplace age gate; session-wide verification is stored separately. */
const HOME_AGE_GATE_SLUG = "__home__";

const CART_TIP_AUTODISMISS_MS = 3_000;

function normalizeQuery(q: string): string {
  return q.trim().toLowerCase();
}

function entryMatches(entry: PublicDispensaryCatalogEntry, q: string): boolean {
  if (!q) return false;
  const hay = [entry.displayName, entry.city, entry.state, entry.publicSlug].join(" ").toLowerCase();
  return hay.includes(q);
}

function itemMatchesQuery(item: PublicMenuLineItem, q: string): boolean {
  if (!q) return false;
  const hay = [item.productName, item.productCategory, item.productStrain ?? "", item.metrcPackageUid]
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
}

function flattenShops(blocks: ShopBlock[]): MarketplaceItem[] {
  const out: MarketplaceItem[] = [];
  for (const block of blocks) {
    if (!block.menu) continue;
    for (const item of block.menu.items) {
      out.push({
        ...item,
        dispensarySlug: block.entry.publicSlug,
        dispensaryDisplayName: block.entry.displayName,
      });
    }
  }
  return out;
}

function orderedCategories(items: MarketplaceItem[]): ProductCategory[] {
  const present = new Set<ProductCategory>();
  for (const item of items) {
    present.add(normalizeProductCategory(item.productCategory, item.productName));
  }
  return CANONICAL_PRODUCT_CATEGORIES.filter((c) => present.has(c));
}

export function MarketplaceScreen() {
  const navigate = useNavigate();
  /** Read filter state straight from the URL (mirrors the address bar; back/forward "just works"). */
  const searchString = useRouterState({
    select: (s) => (s.location.pathname === "/" ? s.location.search : ""),
  });
  const params = useMemo(() => new URLSearchParams(searchString), [searchString]);
  const searchQuery = params.get("q")?.trim() ?? "";
  const activeCategory = params.get("cat")?.trim() ?? "all";
  const activeDispensary = params.get("disp")?.trim() ?? "all";

  const [browseVerified, setBrowseVerified] = useState(() => isSessionAgeVerified());
  const [shops, setShops] = useState<ShopBlock[]>([]);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [cartTip, setCartTip] = useState<{ kind: "ok" | "warn"; message: string } | null>(null);
  const [searchDraft, setSearchDraft] = useState(searchQuery);
  const addOrIncrement = useCartStore((s) => s.addOrIncrement);
  const reconcileCartWithMenu = useCartStore((s) => s.reconcileCartWithMenu);

  /** Keep the visible input in sync when ?q= changes externally (e.g., back-button). */
  useEffect(() => {
    setSearchDraft(searchQuery);
  }, [searchQuery]);

  /** Auto-dismiss the cart tip so it doesn't pile up on rapid adds. */
  useEffect(() => {
    if (!cartTip) return;
    const id = window.setTimeout(() => setCartTip(null), CART_TIP_AUTODISMISS_MS);
    return () => window.clearTimeout(id);
  }, [cartTip]);

  const load = useCallback(async () => {
    if (!isSessionAgeVerified()) return;
    setLoading(true);
    setCatalogError(null);
    try {
      const catalog = await fetchPublicDispensaryCatalog();
      const blocks = await Promise.all(
        catalog.dispensaries.map(async (entry): Promise<ShopBlock> => {
          try {
            const menu = await fetchDispensaryMenu(entry.publicSlug);
            const d = menu.dispensary;
            /**
             * Per-shop invariants must continue to fire inside the per-shop fetch loop —
             * `writeCheckoutShopMeta` and `reconcileCartWithMenu` are slug-keyed and can't
             * run on the flattened render array (redline 02 implementation note 5).
             */
            if (d && typeof d.id === "string" && typeof d.publicSlug === "string" && d.publicSlug.length > 0) {
              writeCheckoutShopMeta(entry.publicSlug, { dispensaryId: d.id, publicSlug: d.publicSlug });
            }
            reconcileCartWithMenu(entry.publicSlug, menu.items);
            return { entry, menu };
          } catch (e) {
            let msg = "Menu could not be loaded.";
            if (e instanceof DispensaryMenuNotFoundError) {
              msg = "Shop not found or not published.";
            } else if (e instanceof DispensaryMenuHttpError && e.apiHint) {
              msg = e.apiHint;
            } else if (e instanceof DispensaryMenuHttpError) {
              msg = `API returned HTTP ${e.status}.`;
            }
            return { entry, menu: null, loadError: msg };
          }
        }),
      );
      setShops(blocks);
    } catch {
      setCatalogError("Could not load the shop directory. Ensure the API is running and try again.");
      setShops([]);
    } finally {
      setLoading(false);
    }
  }, [reconcileCartWithMenu]);

  useEffect(() => {
    if (!browseVerified) return;
    void load();
  }, [browseVerified, load]);

  const allItems = useMemo(() => flattenShops(shops), [shops]);
  const dispensaries = useMemo(
    () => shops.map((s) => s.entry).sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [shops],
  );
  const categories = useMemo(() => orderedCategories(allItems), [allItems]);

  const filteredItems = useMemo(() => {
    const q = normalizeQuery(searchQuery);
    return allItems.filter((item) => {
      if (
        activeCategory !== "all" &&
        normalizeProductCategory(item.productCategory, item.productName) !== activeCategory
      ) {
        return false;
      }
      if (activeDispensary !== "all" && item.dispensarySlug !== activeDispensary) {
        return false;
      }
      if (q) {
        if (itemMatchesQuery(item, q)) return true;
        const block = shops.find((s) => s.entry.publicSlug === item.dispensarySlug);
        if (block && entryMatches(block.entry, q)) return true;
        return false;
      }
      return true;
    });
  }, [allItems, activeCategory, activeDispensary, searchQuery, shops]);

  function setFilterParam(key: "q" | "cat" | "disp", value: string) {
    void navigate({
      to: "/",
      search: (prev) => {
        const next: Record<string, string> = { ...(prev as Record<string, string>) };
        const trimmed = value.trim();
        if (trimmed && !(key !== "q" && trimmed === "all")) {
          next[key] = trimmed;
        } else {
          delete next[key];
        }
        return next;
      },
      replace: true,
    });
  }

  function commitSearch(v: string) {
    setFilterParam("q", v);
  }

  function handleAddToCart(item: PublicMenuLineItem) {
    /** Locate the dispensary slug for the add — `MarketplaceItem` always carries it. */
    const slug = (item as MarketplaceItem).dispensarySlug;
    if (!slug) return;
    const r = addOrIncrement(slug, item, 1);
    if (r.ok) {
      setCartTip({ kind: "ok", message: "Added to cart." });
    } else {
      setCartTip({ kind: "warn", message: r.reason });
    }
  }

  if (!browseVerified) {
    return (
      <AgeGateScreen
        dispensarySlug={HOME_AGE_GATE_SLUG}
        onVerified={() => setBrowseVerified(true)}
      />
    );
  }

  const filtersActive =
    searchQuery.length > 0 || activeCategory !== "all" || activeDispensary !== "all";

  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-6 pt-6">
      {/* Quiet H1 — landmark navigation needs an h1 even when the layout is filter-driven. */}
      <h1 className="text-xs font-semibold uppercase tracking-[.1em] text-[var(--text-secondary)]">
        Marketplace
      </h1>

      <div className="space-y-4">
        <label className="block">
          <span className="sr-only">Search products and shops</span>
          <input
            type="search"
            name="q"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitSearch(e.currentTarget.value);
              }
            }}
            onBlur={(e) => commitSearch(e.currentTarget.value)}
            placeholder="Search products & shops"
            autoComplete="off"
            className="h-11 w-full rounded-full border border-[var(--card-border)] bg-white px-5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-placeholder)] focus:border-[var(--green)] focus:outline-none focus:ring-1 focus:ring-[var(--green)]"
          />
        </label>
      </div>

      {/* Sticky on tablet+ (the filters are the persistent navigation surface).
       *  Mobile keeps them inline at the top — the bottom-tabs already eat the
       *  bottom; pinning them at the top would crowd the small viewport. */}
      <div className="space-y-3 md:sticky md:top-16 md:z-20 md:-mx-4 md:bg-[var(--page-bg)] md:px-4 md:py-3 lg:-mx-7 lg:px-7">
        <FilterPillGroup
          label="Category"
          value={activeCategory}
          onChange={(v) => setFilterParam("cat", v)}
          options={[
            { value: "all", label: "All" },
            ...categories.map((c) => ({ value: c, label: c })),
          ]}
        />
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-[.1em] text-[var(--text-secondary)]">
            Dispensary
          </span>
          <select
            value={activeDispensary}
            onChange={(e) => {
              const v = e.target.value;
              /**
               * Picking a specific shop goes to its store page, where prices
               * are shown (COMP-001: prices only inside a specific dispensary
               * — the marketplace grid hides them, so filtering in place
               * would strand the customer on a priceless view).
               */
              if (v !== "all") {
                void navigate({ to: "/$dispensarySlug/menu", params: { dispensarySlug: v } });
                return;
              }
              setFilterParam("disp", v);
            }}
            className="rounded-full border border-[var(--card-border)] bg-white px-3 py-1.5 text-sm font-semibold text-[var(--text-primary)] shadow-sm focus:border-[var(--green)] focus:outline-none focus:ring-2 focus:ring-[var(--green)]/20"
            aria-label="Go to a dispensary's store page"
          >
            <option value="all">All dispensaries</option>
            {dispensaries.map((d) => (
              <option key={d.publicSlug} value={d.publicSlug}>
                {d.displayName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {cartTip ? (
        <div
          role="status"
          className={`inline-flex items-center rounded-full px-4 py-1.5 text-sm font-medium ${
            cartTip.kind === "ok"
              ? "bg-[var(--surface-sage)] text-[var(--green-dark)]"
              : "bg-amber-50 text-amber-900"
          }`}
        >
          {cartTip.message}
        </div>
      ) : null}

      {catalogError ? (
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-6 shadow-cardMd">
          <p className="text-sm text-[var(--badge-red)]">{catalogError}</p>
          <button
            type="button"
            className="mt-3 rounded-lg bg-[var(--green)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--green-dark)]"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      ) : loading && shops.length === 0 ? (
        <ul
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4"
          aria-busy="true"
        >
          {Array.from({ length: 8 }, (_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </ul>
      ) : filteredItems.length === 0 ? (
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-8 text-center shadow-cardMd">
          <p className="text-sm text-[var(--text-secondary)]">
            {filtersActive
              ? "No products match these filters."
              : "No products are available yet. Add active dispensaries with menus in the API to see them here."}
          </p>
          {filtersActive ? (
            <button
              type="button"
              onClick={() => {
                setSearchDraft("");
                void navigate({ to: "/", search: {}, replace: true });
              }}
              className="mt-4 rounded-lg border border-[var(--card-border)] bg-white px-4 py-2 text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--page-bg)]"
            >
              Clear filters
            </button>
          ) : null}
        </div>
      ) : (
        <ProductGrid items={filteredItems} onAddToCart={handleAddToCart} />
      )}
    </div>
  );
}
